import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Iconify from 'src/components/iconify';
import { formatAmount } from 'src/utils/formatNumber';
import StatusBadge from 'src/features/transactions/StatusBadge';
import { shortAddress } from 'src/utils/shortAddress';
import { config } from 'src/config-global';
import { getConfirmationProgress } from 'src/constants/transactionStatus';

function getConfirmText(tx) {
  const progress = getConfirmationProgress(tx);
  if (!progress) return null;
  return `(${progress})`;
}

const TxRow = memo(
  function TxRow({ tx, redirect }) {
    const confirmText = useMemo(
      () => getConfirmText(tx),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [tx.tokenIn, tx.status, tx.tokenOut, tx.depositConfirmations, tx.payoutConfirmations]
    );

    const handleRedirect = (e) => {
      e.stopPropagation();
      redirect?.(tx);
    };

    function renderHashes() {
      if (!tx.txHashIn && !tx.txHashOut) {
        return (
          <Typography variant="caption" color="text.disabled">
            -
          </Typography>
        );
      }

      return (
        <Stack spacing={0.5}>
          {tx.txHashIn && (
            <Button
              href={`${config.etherscanEndPoint}/tx/${tx.txHashIn}`}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <Typography fontFamily="monospace" variant="caption">
                Tx in: {shortAddress(tx.txHashIn)} <Iconify icon="majesticons:open" />
              </Typography>
            </Button>
          )}

          {tx.txHashOut && (
            <Button
              href={`${config.etherscanEndPoint}/tx/${tx.txHashOut}`}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <Typography fontFamily="monospace" variant="caption">
                Tx out: {shortAddress(tx.txHashOut)} <Iconify icon="majesticons:open" />
              </Typography>
            </Button>
          )}
        </Stack>
      );
    }

    function renderAction() {
      if (!redirect) {
        return (
          <Typography variant="caption" color="text.disabled">
            -
          </Typography>
        );
      }

      return (
        <Button
          size="small"
          variant="outlined"
          onClick={handleRedirect}
          sx={{ minHeight: 28, px: 1, py: 0 }}
          startIcon={<Iconify icon="solar:eye-bold" width={16} />}
        >
          View
        </Button>
      );
    }

    return (
      <TableRow hover>
        <TableCell align="center">
          <StatusBadge status={tx.status} size="sm" />
          <Stack>
            <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {confirmText}
            </Typography>
          </Stack>
          {(tx.status === 'underpaid_refund_pending' ||
            tx.status === 'overpaid_refund_pending') && (
            <Typography variant="caption" color="text.secondary">
              Amount will be refunded
            </Typography>
          )}
        </TableCell>

        <TableCell align="center">
          <Typography fontWeight={600}>
            {formatAmount(tx.amountIn)} {tx.tokenIn?.toUpperCase()}
          </Typography>

          {tx.tokenOut && (
            <>
              <Iconify icon="mdi:exchange" />
              <Typography fontWeight={600}>
                {formatAmount(tx.amountOut)} {tx.tokenOut.toUpperCase()}
              </Typography>
            </>
          )}
        </TableCell>

        <TableCell align="center">
          <Stack spacing={0.5}>
            <Typography variant="body2">{new Date(tx.createdAt).toLocaleDateString()}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(tx.createdAt).toLocaleTimeString()}
            </Typography>
          </Stack>
        </TableCell>

        <TableCell align="center">{renderHashes()}</TableCell>

        <TableCell align="center">{renderAction()}</TableCell>
      </TableRow>
    );
  },
  (prev, next) => {
    const a = prev.tx;
    const b = next.tx;

    return (
      a._id === b._id &&
      a.status === b.status &&
      a.depositConfirmations === b.depositConfirmations &&
      a.payoutConfirmations === b.payoutConfirmations &&
      a.amountIn === b.amountIn &&
      a.amountOut === b.amountOut &&
      a.tokenIn === b.tokenIn &&
      a.tokenOut === b.tokenOut &&
      a.txHashIn === b.txHashIn &&
      a.txHashOut === b.txHashOut &&
      a.from === b.from &&
      a.to === b.to &&
      a.createdAt === b.createdAt &&
      a.updatedAt === b.updatedAt &&
      prev.redirect === next.redirect
    );
  }
);

TxRow.propTypes = {
  redirect: PropTypes.func,
  tx: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    status: PropTypes.string.isRequired,
    depositConfirmations: PropTypes.number,
    payoutConfirmations: PropTypes.number,
    amountIn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    amountOut: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tokenIn: PropTypes.string.isRequired,
    tokenOut: PropTypes.string,
    fees: PropTypes.object,
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    txHashIn: PropTypes.string,
    txHashOut: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
  }).isRequired,
};

export default TxRow;
